"use client";
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

export default function CategoriesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtitleRef = useRef(null);
  const descriptionRef = useRef(null);
  const categoriesRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

    // Master timeline for heading/subtitle/description
    const masterTl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top 70%",
        end: "center center",
        toggleActions: "play none none reverse",
      },
    });

    // ----- Heading Animation -----
    const headingElement = headingRef.current;
    const headingText = headingElement.textContent;
    headingElement.textContent = "";
    headingText.split(" ").forEach((word) => {
      const wordSpan = document.createElement("span");
      wordSpan.className = "word-wrapper inline-block";
      word.split("").forEach((char) => {
        const charSpan = document.createElement("span");
        charSpan.className = "char relative inline-block";
        charSpan.textContent = char;
        wordSpan.appendChild(charSpan);
      });
      headingElement.appendChild(wordSpan);
      headingElement.appendChild(document.createTextNode(" "));
    });
    const chars = headingElement.querySelectorAll(".char");

    masterTl
      .from(chars, {
        immediateRender: false,
        opacity: 0,
        y: 100,
        rotationX: -90,
        stagger: 0.02,
        duration: 1,
        ease: "back.out(1.7)",
      })
      .from(
        subtitleRef.current,
        {
          immediateRender: false,
          opacity: 0,
          y: 20,
          duration: 0.8,
          ease: "power2.out",
        },
        "-=0.5"
      )
      .from(
        ".divider",
        {
          immediateRender: false,
          scaleX: 0,
          duration: 1.2,
          ease: "power3.inOut",
        },
        "-=0.7"
      )
      .from(
        descriptionRef.current,
        {
          immediateRender: false,
          opacity: 0,
          y: 30,
          duration: 1,
          ease: "power2.out",
        },
        "-=1"
      );

    // ----- Categories 3D Entrance Animation -----
    const categoryItems = gsap.utils.toArray(".category-item");
    const categoriesTl = gsap.timeline({
      scrollTrigger: {
        trigger: categoriesRef.current,
        start: "top 75%",
        toggleActions: "play none none reverse",
      },
    });

    categoriesTl.from(categoryItems, {
      immediateRender: false,
      opacity: 0,
      y: 50,
      duration: 1.2,
      stagger: 0.05,
      ease: "power3.out",
    });

    // Parallax on background elements
    gsap.to(".bg-element", {
      y: gsap.utils.wrap([-200, -100, -150]),
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1.5,
      },
    });

    // Store ref
    tlRef.current = masterTl;

    return () => {
      if (tlRef.current) tlRef.current.kill();
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, []);

  const categories = [
    "Einfachkernrohre",
    "Doppelkernrohre",
    "Rammkernrohre",
    "Seilkernrohre",
    "Schraubbohrgarnituren",
    "Bohrgestänge",
    "Futterrohre",
    "Bohrrohre",
    "Bohrkronen, Rohrschuhe",
    "Flügelbohrer, Stufenmeißel",
    "Steinfangbohrer",
    "Übergänge",
    "Stabilisatoren",
    "Schwerstangen",
    "Fangwerkzeuge",
    "Kernfangringe, Rammfangringe",
    "Spannbacken, Spanneinsätze",
    "Fangklinken, Fangpilze",
    "Kolbenstangen",
    "Zylinderlaufbuchsen",
    "Bohrschnecken + Zubehör",
    "Kiespumpen, Schappen",
  ];

  return (
    <section
      ref={sectionRef}
      id="categories"
      className="pt-16 pb-40 relative overflow-hidden"
      style={{ background: "#000" }} // pure black background
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="bg-element absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#47B9BB] opacity-[0.03] blur-3xl"></div>
        <div className="bg-element absolute top-1/3 -right-40 w-80 h-80 rounded-full bg-[#47B9BB] opacity-[0.04] blur-3xl"></div>
        <div className="bg-element absolute bottom-10 left-1/4 w-64 h-64 rounded-full bg-[#3a596f] opacity-[0.03] blur-3xl"></div>
        {/* Grid lines for tech effect */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXr1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNNjAgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXptLTIgMHYxaC0xVjBoMXpNMSAwdjFIMHYtMWgxWm01OCAxNXYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxem0tMiAwdjFoLTF2LTFoMXptLTIgMHYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxek0xIDMwdjFIMHYtMWgxWk01OSA0NXYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxem0tMiAwdjFoLTF2LTFoMXptLTIgMHYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxek0xIDQ1djFIMHYtMWgxWk02MCA1OXYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxem0tMiAwdjFoLTF2LTFoMXptLTIgMHYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxem0tMiAwdjFoLTF2LTFoMXptLTIgMHYxaC0xdi0xaDF6bS0yIDB2MWgtMXYtMWgxek0xIDQ1djFIMHYtMWgxeiIgZmlsbD0iIzQ3QjlCQiIgZmlsbC1vcGFjaXR5PSIuMDMiIGZpbGwtcnVsZT0ibm9uemVybyIvPjwvZz48L3N2Zz4=')] opacity-10"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Heading Section */}
        <div className="text-center mb-12">
          <div className="inline-block relative">
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 w-20 h-20">
              <svg viewBox="0 0 100 100" className="w-full h-full opacity-10 text-[#47B9BB]">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="6 4"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </svg>
            </div>
            <h2
              ref={headingRef}
              className="text-5xl font-bold text-white mb-6 tracking-tight leading-tight"
            >
              Geologische Ausrüstungen - Bohrausrüstungen
            </h2>
          </div>
          <p
            ref={subtitleRef}
            className="subtitle text-2xl text-gray-300 font-light mb-8"
          >
            Kernrohre - Bohrgarnituren - Fangringe
          </p>
          <div className="divider w-32 h-1 mx-auto bg-gradient-to-r from-transparent via-[#47B9BB] to-transparent opacity-70"></div>
          <p
            ref={descriptionRef}
            className="description mt-12 text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            Die Firma "G-A-B-S GmbH" bietet eine Vielzahl von Erzeugnissen für die Bohrindustrie an. Im Folgenden stellen wir Ihnen in einer Übersicht der Produktgruppen zusammen ...
          </p>
        </div>

        {/* Subtle lines / decorative elements */}
        <div className="relative mb-12">
          <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#47B9BB] to-transparent opacity-30">
            <div className="absolute top-0 left-0 w-20 h-px bg-[#47B9BB] animate-[rightToLeft_8s_linear_infinite]"></div>
          </div>
        </div>

        {/* Categories List (text style) */}
        <div
          ref={categoriesRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-8 gap-x-6"
        >
          {categories.map((item, idx) => (
            <div key={idx} className="category-item text-gray-200 border-b border-gray-700 pb-2">
              <p className="text-lg font-semibold tracking-wide">
                {item}
              </p>
              <span className="block mt-1 text-sm text-gray-500">
                GB-{(idx + 1) * 25}-{Math.floor(Math.random() * 100)}
              </span>
            </div>
          ))}
        </div>

        {/* Bottom Section / CTA */}
        <div className="mt-24 text-center relative">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#47B9BB] to-transparent opacity-30"></div>
          <button className="relative group bg-transparent hover:bg-[#47B9BB] text-[#47B9BB] hover:text-white py-3 px-8 rounded-full transition-all duration-300 overflow-hidden">
            <span className="relative z-10">Alle Produkte anzeigen</span>
            <div className="absolute inset-0 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 bg-[#47B9BB] -z-0"></div>
            <div className="absolute top-0 left-0 w-full h-full border border-[#47B9BB] rounded-full"></div>
          </button>
        </div>
      </div>

      {/* Keyframes for animated lines */}
      <style>{`
        @keyframes rightToLeft {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes leftToRight {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </section>
  );
}
